import { v4 as uuidv4 } from 'uuid';
import * as THREE from 'three';
import type { SketchFace, SketchLooseEdge, SketchSolid, SketchVec3 } from './types';

/**
 * Sandbox (terrain) engine — TIN landscapes inside the face-based sketch
 * document.
 *
 * A terrain is a regular SketchSolid whose top shell is a triangulated
 * grid (TIN): border vertices stay at the base plane so the four skirt
 * walls and the bottom slab keep the shell closed, and every Sandbox tool
 * just rewrites vertex heights through one history action.
 *
 * Grid resolution is deliberately modest (SketchUp's sandbox default is a
 * ~10×10 grid): the document renders one mesh per face, so terrain counts
 * its triangles in the hundreds, not thousands.
 */

const asTuple = (p: THREE.Vector3): SketchVec3 => [p.x, p.y, p.z];
const v3 = (p: SketchVec3): THREE.Vector3 => new THREE.Vector3(...p);

/** Vertex identity key (vertices are shared across adjacent triangles). */
const vKey = (p: THREE.Vector3 | SketchVec3): string => {
    const v = p instanceof THREE.Vector3 ? p : v3(p);
    return `${Math.round(v.x * 1e4)}|${Math.round(v.y * 1e4)}|${Math.round(v.z * 1e4)}`;
};

interface QuadCorner { a: THREE.Vector3; b: THREE.Vector3; c: THREE.Vector3; d: THREE.Vector3 }

/** Planar quad face from an ordered CCW corner loop (seen from the normal). */
function quadFace(prefix: string, corners: QuadCorner, materialId: string): SketchFace {
    const normal = new THREE.Vector3()
        .crossVectors(corners.b.clone().sub(corners.a), corners.d.clone().sub(corners.a))
        .normalize();
    return {
        id: `${prefix}-${uuidv4()}`,
        origin: asTuple(corners.a),
        normal: asTuple(normal),
        outer: [asTuple(corners.a), asTuple(corners.b), asTuple(corners.c), asTuple(corners.d)],
        holes: [],
        materialId,
    };
}

export interface TerrainBuild {
    solid: SketchSolid;
    /** Grid samples for tools that reshape the surface later. */
    heights: number[][];
}

/**
 * Build a closed terrain slab: triangulated top TIN over [width × depth]
 * with `cols × rows` cells, skirt walls and a bottom slab. `heightAt(u, v)`
 * receives normalized 0..1 grid coordinates; the border is forced to the
 * base plane (origin) so the skirt stays planar.
 */
export function buildTerrainSolid(
    origin: THREE.Vector3,
    width: number,
    depth: number,
    cols: number,
    rows: number,
    heightAt: (u: number, v: number) => number,
    materialId = 'grass',
    name = 'Terrain'
): TerrainBuild {
    const baseZ = origin.z;
    const heights: number[][] = [];
    for (let j = 0; j <= rows; j++) {
        const row: number[] = [];
        for (let i = 0; i <= cols; i++) {
            const border = i === 0 || j === 0 || i === cols || j === rows;
            row.push(border ? baseZ : heightAt(i / cols, j / rows));
        }
        heights.push(row);
    }
    const px = (i: number): number => origin.x + (i / cols) * width;
    const py = (j: number): number => origin.y + (j / rows) * depth;
    const pt = (i: number, j: number): THREE.Vector3 => new THREE.Vector3(px(i), py(j), heights[j][i]);

    const bottomZ = baseZ - Math.max(width, depth, 1) * 0.05;
    const faces: SketchFace[] = [];

    // Top TIN: two up-facing triangles per cell.
    for (let j = 0; j < rows; j++) {
        for (let i = 0; i < cols; i++) {
            const p00 = pt(i, j);
            const p10 = pt(i + 1, j);
            const p11 = pt(i + 1, j + 1);
            const p01 = pt(i, j + 1);
            const tri = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3): SketchFace => ({
                id: `tin-${uuidv4()}`,
                origin: asTuple(a),
                normal: asTuple(new THREE.Vector3().crossVectors(b.clone().sub(a), c.clone().sub(a)).normalize()),
                outer: [asTuple(a), asTuple(b), asTuple(c)],
                holes: [],
                materialId,
            });
            faces.push(tri(p00, p10, p11));
            faces.push(tri(p00, p11, p01));
        }
    }

    // Skirt walls (outward winding) + bottom slab.
    const zt = baseZ;
    faces.push(quadFace('skirt', { a: new THREE.Vector3(px(cols), py(0), zt), b: new THREE.Vector3(px(0), py(0), zt), c: new THREE.Vector3(px(0), py(0), bottomZ), d: new THREE.Vector3(px(cols), py(0), bottomZ) }, materialId));
    faces.push(quadFace('skirt', { a: new THREE.Vector3(px(0), py(rows), zt), b: new THREE.Vector3(px(cols), py(rows), zt), c: new THREE.Vector3(px(cols), py(rows), bottomZ), d: new THREE.Vector3(px(0), py(rows), bottomZ) }, materialId));
    faces.push(quadFace('skirt', { a: new THREE.Vector3(px(0), py(0), zt), b: new THREE.Vector3(px(0), py(rows), zt), c: new THREE.Vector3(px(0), py(rows), bottomZ), d: new THREE.Vector3(px(0), py(0), bottomZ) }, materialId));
    faces.push(quadFace('skirt', { a: new THREE.Vector3(px(cols), py(rows), zt), b: new THREE.Vector3(px(cols), py(0), zt), c: new THREE.Vector3(px(cols), py(0), bottomZ), d: new THREE.Vector3(px(cols), py(rows), bottomZ) }, materialId));
    faces.push(quadFace('bottom', { a: new THREE.Vector3(px(0), py(0), bottomZ), b: new THREE.Vector3(px(0), py(rows), bottomZ), c: new THREE.Vector3(px(cols), py(rows), bottomZ), d: new THREE.Vector3(px(cols), py(0), bottomZ) }, materialId));

    return {
        solid: {
            id: uuidv4(),
            name,
            faces,
            materialId,
            visible: true,
            locked: false,
        },
        heights,
    };
}

/** Is this solid a Sandbox terrain? (top shell is an up-facing TIN). */
export function isTerrain(solid: SketchSolid): boolean {
    return solid.faces.some((f) => new THREE.Vector3(...f.normal).normalize().z > 0.9 && f.outer.length === 3);
}

/** Barycentric height of the TIN surface at (x, y); null outside the grid. */
export function terrainHeightAt(solid: SketchSolid, x: number, y: number): number | null {
    const point = new THREE.Vector2(x, y);
    for (const face of solid.faces) {
        const n = new THREE.Vector3(...face.normal).normalize();
        if (n.z <= 0.9 || face.outer.length !== 3) continue;
        const a = v3(face.outer[0]);
        const b = v3(face.outer[1]);
        const c = v3(face.outer[2]);
        const denom = (b.y - c.y) * (a.x - c.x) + (c.x - b.x) * (a.y - c.y);
        if (Math.abs(denom) < 1e-12) continue;
        const l1 = ((b.y - c.y) * (point.x - c.x) + (c.x - b.x) * (point.y - c.y)) / denom;
        const l2 = ((c.y - a.y) * (point.x - c.x) + (a.x - c.x) * (point.y - c.y)) / denom;
        const l3 = 1 - l1 - l2;
        if (l1 < -1e-6 || l2 < -1e-6 || l3 < -1e-6) continue;
        return l1 * a.z + l2 * b.z + l3 * c.z;
    }
    return null;
}

/**
 * Rewrite every vertex in `moves` (key → new position) consistently across
 * all faces of the solid. Pure: returns the updated solid.
 */
export function moveTerrainVertices(
    solid: SketchSolid,
    moves: Map<string, THREE.Vector3>
): SketchSolid {
    if (moves.size === 0) return solid;
    const remap = (loop: SketchVec3[]): SketchVec3[] =>
        loop.map((p) => {
            const next = moves.get(vKey(p));
            return next ? asTuple(next) : p;
        });
    const faces = solid.faces.map((face) => {
        let touched = false;
        const outer = face.outer.map((p) => {
            const next = moves.get(vKey(p));
            if (next) touched = true;
            return next ? asTuple(next) : p;
        });
        if (!touched) return face;
        return {
            ...face,
            origin: outer[0],
            outer,
            holes: face.holes.map(remap),
        };
    });
    return { ...solid, faces };
}

/**
 * Smoove: circular-falloff lift/drop of the TIN around `center`.
 * Positive delta pulls up, negative pushes down; the falloff is smooth
 * (cosine) so the bump blends into the surrounding grid.
 */
export function smooveTerrain(
    solid: SketchSolid,
    center: THREE.Vector3,
    radius: number,
    delta: number
): SketchSolid {
    const moves = new Map<string, THREE.Vector3>();
    const seen = new Set<string>();
    for (const face of solid.faces) {
        const n = new THREE.Vector3(...face.normal).normalize();
        if (n.z <= 0.9) continue;
        for (const p of face.outer) {
            const key = vKey(p);
            if (seen.has(key)) continue;
            seen.add(key);
            const v = v3(p);
            const d = Math.hypot(v.x - center.x, v.y - center.y);
            if (d >= radius) continue;
            const falloff = 0.5 * (1 + Math.cos((d / radius) * Math.PI));
            moves.set(key, v.clone().setZ(v.z + delta * falloff));
        }
    }
    return moveTerrainVertices(solid, moves);
}

/**
 * Stamp: press a flat pad of `radius` at height `padZ` into the terrain.
 * Inside the core radius the surface is clamped exactly to the pad height;
 * a transition band blends the pad edge into the existing terrain.
 */
export function stampTerrain(
    solid: SketchSolid,
    center: THREE.Vector3,
    radius: number,
    padZ: number
): SketchSolid {
    const moves = new Map<string, THREE.Vector3>();
    const seen = new Set<string>();
    const core = radius * 0.6;
    for (const face of solid.faces) {
        const n = new THREE.Vector3(...face.normal).normalize();
        if (n.z <= 0.9) continue;
        for (const p of face.outer) {
            const key = vKey(p);
            if (seen.has(key)) continue;
            seen.add(key);
            const v = v3(p);
            const d = Math.hypot(v.x - center.x, v.y - center.y);
            if (d >= radius) continue;
            const blend = d <= core ? 1 : 0.5 * (1 + Math.cos(((d - core) / (radius - core)) * Math.PI));
            const z = v.z + (padZ - v.z) * blend;
            moves.set(key, v.clone().setZ(z));
        }
    }
    return moveTerrainVertices(solid, moves);
}

/** Add Detail: split the picked TIN triangle around the picked point. */
export function addTerrainDetail(
    solid: SketchSolid,
    faceId: string,
    point: THREE.Vector3
): SketchSolid | null {
    const face = solid.faces.find((f) => f.id === faceId);
    if (!face || face.outer.length !== 3) return null;
    // Keep the inserted vertex ON the triangle's plane.
    const a = v3(face.outer[0]);
    const n = new THREE.Vector3(...face.normal).normalize();
    const projected = point.clone().sub(a).projectOnPlane(n).add(a);
    const loop = face.outer.map((p) => v3(p));
    const faces: SketchFace[] = [];
    for (let i = 0; i < 3; i++) {
        const p1 = loop[i];
        const p2 = loop[(i + 1) % 3];
        faces.push({
            id: `tin-${uuidv4()}`,
            origin: asTuple(p1),
            normal: [...face.normal] as SketchVec3,
            outer: [asTuple(p1), asTuple(p2), asTuple(projected)],
            holes: [],
            materialId: face.materialId,
        });
    }
    return { ...solid, faces: solid.faces.map((f) => (f.id === faceId ? faces : f)).flat() };
}

/**
 * Drape: project loose edges vertically onto the TIN. Each edge becomes a
 * chain of on-surface segments (the original edge stays — the user erases
 * it if the projection should replace it).
 */
export function drapeEdges(
    solid: SketchSolid,
    edges: SketchLooseEdge[]
): SketchVec3[][] {
    const polylines: SketchVec3[][] = [];
    for (const edge of edges) {
        const a = v3(edge.a);
        const b = v3(edge.b);
        const length = a.distanceTo(b);
        if (length < 1e-9) continue;
        const samples = Math.min(32, Math.max(2, Math.ceil(length / 0.5) + 1));
        const chain: THREE.Vector3[] = [];
        for (let i = 0; i < samples; i++) {
            const p = a.clone().lerp(b, i / (samples - 1));
            const z = terrainHeightAt(solid, p.x, p.y);
            chain.push(new THREE.Vector3(p.x, p.y, z ?? p.z));
        }
        polylines.push(chain.map(asTuple));
    }
    return polylines;
}

/**
 * From Contours: skin a terrain through loose contour edges. The grid
 * samples the contours' heights via inverse-distance weighting over the k
 * nearest contour vertices; the border falls back to the lowest contour so
 * the skirt closes.
 */
export function terrainFromContours(
    edges: SketchLooseEdge[],
    cellSize: number
): SketchSolid | null {
    const points: THREE.Vector3[] = [];
    let found = false;
    const box = new THREE.Box3();
    for (const edge of edges) {
        for (const p of [edge.a, edge.b]) {
            const v = v3(p);
            points.push(v);
            box.expandByPoint(v);
            found = true;
        }
    }
    if (!found || points.length < 3) return null;
    box.expandByScalar(-Math.min(cellSize, 1)); // pad inward; borders clamp anyway

    const width = Math.max(box.max.x - box.min.x, cellSize * 2);
    const depth = Math.max(box.max.y - box.min.y, cellSize * 2);
    const cols = Math.min(20, Math.max(2, Math.round(width / cellSize)));
    const rows = Math.min(20, Math.max(2, Math.round(depth / cellSize)));

    const baseZ = box.min.z;
    const sampled: Array<[number, number, number]> = points.map((p) => [p.x, p.y, p.z]);
    const k = Math.min(8, sampled.length);
    const heightAt = (u: number, v: number): number => {
        const x = box.min.x + u * width;
        const y = box.min.y + v * depth;
        // k nearest contour vertices, inverse-distance (power 2) weighting.
        const nearest = sampled
            .map((s) => ({ z: s[2], d: (s[0] - x) * (s[0] - x) + (s[1] - y) * (s[1] - y) }))
            .sort((p, q) => p.d - q.d)
            .slice(0, k);
        let wSum = 0;
        let zSum = 0;
        for (const s of nearest) {
            const w = 1 / Math.max(s.d, 1e-6);
            wSum += w;
            zSum += s.z * w;
        }
        return zSum / wSum;
    };

    const { solid } = buildTerrainSolid(
        new THREE.Vector3(box.min.x, box.min.y, baseZ),
        width,
        depth,
        cols,
        rows,
        heightAt,
        'grass',
        'Terrain From Contours'
    );
    return solid;
}
